import "./Markdown.scss";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import remarkDirective from "remark-directive";
import rehypeRaw from "rehype-raw";
import { mentionsPlugin } from "./remark-plugins/mentions";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { okaidia as style } from "react-syntax-highlighter/dist/esm/styles/prism";
import { DocumentedComponent } from "../../../shared/components/DocumentedComponent";
import _ from "lodash";
import { JsxRenderer } from "../ContentRenderer/JsxRenderer";
import { DocumentationSample } from "../DocumentationSample";
import { useEffect, useMemo, useRef, useState } from "react";
import clsx from "clsx";
import { TableOfContents } from "./TableOfContents";
import { getDataProps } from "../../../shared/utilities";

const getElementId = (text: string) => text.toLowerCase().replace(/[^\w]+/g, '-');

const remarkPlugins = [remarkGfm, remarkDirective, mentionsPlugin];
const rehypePlugins = [rehypeRaw];

type MarkdownProps = {
    name?: string;
    key?: string;
    source?: string;
    children?: any;
    disableAnchorGeneration?: boolean;
    renderTableOfContents?: boolean;
    [key: string]: any;
}

function Markdown(props: MarkdownProps) {
    const { key, source, children, disableAnchorGeneration, renderTableOfContents, ...rest } = props;

    // Workaround for the fact that JSX Parser wraps whitespace-enclosed elements in a fragment by default...
    const sanitizedChildren = _.isArray(children) ?
        children.find(c => _.isString(c)) :
        children?.type?.toString()?.indexOf("fragment") > -1 ? children?.children?.[0] :
            children;
    const content = source ?? sanitizedChildren;

    const contentRef = useRef<HTMLDivElement>(null);
    const contentElement = contentRef?.current;

    // Force re-render once-off to ensure that contentElement is available for the Table of Contents to attach itself to...
    const [contentElementIsInDom, setContentElementIsInDom] = useState(false);
    const [renderingHasCompleted, setRenderingHasCompleted] = useState(false);
    useEffect(() => {
        setContentElementIsInDom(true);
    }, []);

    useEffect(() => {
        if (!contentElementIsInDom) return;

        // To prevent flickering/scroll-thrashing during the initial render, we need to monitor the container element for DOM mutations.
        // Once 100ms has elapsed without any mutations, we can assume that the content has finished rendering and we can set the renderingCompleted flag.
        let lastMutation = new Date();
        const observer = new MutationObserver(() => { lastMutation = new Date(); });
        observer.observe(contentElement, { childList: true, subtree: true });
        let intervalId = setInterval(() => {
            if (new Date().getTime() - lastMutation.getTime() > 100) {
                setRenderingHasCompleted(true);
                observer.disconnect();
                clearInterval(intervalId);

                // Once the content has finished rendering, scroll to the relevant position in the document...
                const hash = window.location.hash.slice(1);
                if (hash) {
                    const referencedElement = contentElement.querySelector(`#${hash}`);
                    if (referencedElement) {
                        referencedElement.scrollIntoView({ behavior: "instant" });
                        return;
                    }
                }

                contentElement.scrollTo({ top: 0, behavior: "instant" });
            }
        }, 50);

        return () => {
            observer.disconnect();
        };

    }, [contentElementIsInDom]);

    const components = useMemo(() => ({
        pre: ({ children }) => {
            return children;
        },
        code: ({ node, className, children, ...props }) => {
            const match = /language-(\w+)/.exec(className || '');
            const language = match && match[1];

            if (language === "qerent") {
                return <JsxRenderer template={String(children)} />;
            }

            if (language === "documentation") {
                return <DocumentationSample input={String(children)} />;
            }

            return match ? (
                <SyntaxHighlighter
                    children={String(children).replace(/\n$/, '')}
                    style={style}
                    language={language}
                    PreTag="div"
                    {...props}
                />
            ) : (
                <code className={className} {...props}>
                    {children}
                </code>
            )
        },
        h1: ({ node, children, ...props }) => <h1 {...props} id={getElementId(String(children))}>{disableAnchorGeneration ? children : <a href={"#" + getElementId(String(children))}>{children}</a>}</h1>,
        h2: ({ node, children, ...props }) => <h2 {...props} id={getElementId(String(children))}>{disableAnchorGeneration ? children : <a href={"#" + getElementId(String(children))}>{children}</a>}</h2>,
        h3: ({ node, children, ...props }) => <h3 {...props} id={getElementId(String(children))}>{disableAnchorGeneration ? children : <a href={"#" + getElementId(String(children))}>{children}</a>}</h3>,
        h4: ({ node, children, ...props }) => <h4 {...props} id={getElementId(String(children))}>{disableAnchorGeneration ? children : <a href={"#" + getElementId(String(children))}>{children}</a>}</h4>,
        h5: ({ node, children, ...props }) => <h5 {...props} id={getElementId(String(children))}>{disableAnchorGeneration ? children : <a href={"#" + getElementId(String(children))}>{children}</a>}</h5>,
        h6: ({ node, children, ...props }) => <h6 {...props} id={getElementId(String(children))}>{disableAnchorGeneration ? children : <a href={"#" + getElementId(String(children))}>{children}</a>}</h6>,
    }), [disableAnchorGeneration]);

    return (
        <div className={clsx("Markdown-container", renderTableOfContents && "with-table-of-contents")} style={{ opacity: renderingHasCompleted ? 1 : 0 }} {...getDataProps(props)}>
            <div className="markdown" ref={contentRef}>
                <ReactMarkdown
                    key={key}
                    remarkPlugins={remarkPlugins}
                    rehypePlugins={rehypePlugins}
                    {...rest}
                    components={components}
                    children={content}
                />
            </div>
            {renderTableOfContents && contentElementIsInDom && contentElement && <TableOfContents content={content} contentElement={contentElement} />}
        </div>
    );
}

(Markdown as DocumentedComponent).metadata = {
    description: "The Markdown component renders the provided markdown as HTML.  See the [component's repository](https://github.com/rexxars/react-markdown) for the full documentation.",
    isSelfClosing: false,
    attributes: [
        { name: "name", type: "string", description: "If provided, the underlying component's `data-testid` attribute will be set to this value. This is useful for targeting the element via Playwright, Puppeteer or similar UI testing tools. Can be overridden via an explicit `data-testid` value. Optional - no default value." },
        { name: "disableAnchorGeneration", type: "boolean", description: "By default, the component will automatically generate URL fragment links for each heading.  This can be disabled by setting this prop to `true`." },
        { name: "renderTableOfContents", type: "boolean", description: "If `true`, the component will render a Table of Contents alongside the markdown content.  Optional - defaults to `false`." },
    ]
};

export default Markdown;